import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CheckIcon from '@mui/icons-material/Check';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../../global/theme/tokens';

/**
 * Sort picker for a list that sorts through the API, built like the multi
 * selects so the filter rows of a page look the same.
 *
 * options: [{ value, label, defaultOrder }] - defaultOrder is the direction the
 * field is switched to, so a deadline starts with the nearest one.
 */
const SortSelect = ({ options = [], sortBy, sortOrder = 'asc', onChange }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);

  const active = options.find((option) => option.value === sortBy);

  function handleSelect(option) {
    // picking the active field again only flips the direction
    onChange({
      sortBy: option.value,
      sortOrder:
        option.value === sortBy
          ? sortOrder === 'asc'
            ? 'desc'
            : 'asc'
          : option.defaultOrder || 'asc',
    });
    setAnchorEl(null);
  }

  return (
    <Box className="flex items-center gap-1">
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="flex items-center gap-1 min-w-0"
        sx={{
          borderRadius: '8px',
          paddingY: '4px',
          color: 'inherit',
          textTransform: 'none',
          backgroundColor: colors.bgInput,
          ':hover': { backgroundColor: `${colors.info}20` },
        }}
      >
        <SwapVertIcon sx={{ fontSize: '16px', opacity: 0.6, flexShrink: 0 }} />
        <Typography
          fontSize="12px"
          className="opacity-60 hidden md:block shrink-0"
        >
          {t('Sort')}
        </Typography>
        {/* a long field name is cut off rather than stretching the button */}
        <Typography
          fontSize="12px"
          fontWeight={500}
          noWrap
          sx={{ maxWidth: '110px' }}
        >
          {active ? active.label : t('Default')}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: '18px',
            flexShrink: 0,
            transform: `rotate(${anchorEl ? 180 : 0}deg)`,
            transition: 'transform 200ms',
          }}
        />
      </Button>

      {/* the direction is its own control, so it can be flipped without
          reopening the list */}
      <Tooltip title={sortOrder === 'asc' ? t('Ascending') : t('Descending')}>
        <span>
          <IconButton
            size="small"
            disabled={!sortBy}
            onClick={() =>
              onChange({
                sortBy,
                sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
              })
            }
            sx={{ borderRadius: '8px', backgroundColor: colors.bgInput }}
          >
            {sortOrder === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // the global backdrop blurs everything behind any modal
        sx={{
          '.MuiBackdrop-root': {
            bgcolor: 'transparent',
            background: 'unset',
            backdropFilter: 'unset',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '220px',
              maxWidth: 'min(300px, calc(100vw - 32px))',
              maxHeight: '360px',
              borderRadius: '12px',
              padding: '4px',
              backgroundColor: colors.glassSolid,
              backgroundImage: 'unset',
              border: `1px solid ${colors.text}15`,
              // the global list styles put a border on every item
              '& .MuiMenuItem-root': {
                border: 'none !important',
                borderRadius: '8px',
                gap: 1,
                paddingY: '4px',
                maxWidth: '100%',
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => onChange({ sortBy: null, sortOrder: 'asc' })}>
          <CheckIcon
            sx={{ fontSize: '16px', opacity: sortBy ? 0 : 0.8, flexShrink: 0 }}
          />
          <Typography fontSize="13px" noWrap>
            {t('Default')}
          </Typography>
        </MenuItem>

        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => handleSelect(option)}>
            <CheckIcon
              sx={{
                fontSize: '16px',
                opacity: option.value === sortBy ? 0.8 : 0,
                flexShrink: 0,
              }}
            />
            <Typography fontSize="13px" noWrap title={option.label}>
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </Popover>
    </Box>
  );
};

export default SortSelect;
